import React, { createContext, useContext, useState, useEffect } from "react";
import { collection, query, where, getDoc, addDoc, deleteDoc, doc, updateDoc, serverTimestamp, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./AuthContext";
import { useNotifications } from "./NotificationContext";
import { normalizeRole } from "../utils/roleUtils";

const QueryContext = createContext(null);

export function useQueries() {
    const ctx = useContext(QueryContext);
    if (!ctx) throw new Error("useQueries must be used inside <QueryProvider>");
    return ctx;
}

export function QueryProvider({ children }) {
    const { userProfile } = useAuth();
    const { addNotification } = useNotifications();
    const [myQueries, setMyQueries] = useState([]);
    const [teamQueries, setTeamQueries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isLeadUser = normalizeRole(userProfile?.role) === "lead";

    // ── Listen to user's queries (for employees) ────────────────────────
    useEffect(() => {
        if (!userProfile?.uid) {
            setMyQueries([]);
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, "queries"),
            where("askedBy", "==", userProfile.uid)
        );

        const unsubscribe = onSnapshot(q, 
            (snapshot) => {
                const queriesData = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                // In-memory sort by createdAt descending (handling newly created queries with null server timestamp)
                queriesData.sort((a, b) => {
                    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : Date.now());
                    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : Date.now());
                    return bTime - aTime;
                });
                setMyQueries(queriesData);
                if (!isLeadUser) {
                    setLoading(false);
                }
                setError(null);
            },
            (err) => {
                console.error("Error in my queries listener:", err);
                setError(err.message);
                if (!isLeadUser) {
                    setLoading(false);
                }
            }
        );

        return unsubscribe;
    }, [userProfile?.uid, isLeadUser]);

    // ── Listen to team's queries (for leads) ────────────────────────────
    useEffect(() => {
        if (!userProfile?.uid || !isLeadUser) {
            setTeamQueries([]);
            return;
        }

        // Lead gets queries from their team members
        const q = query(
            collection(db, "queries"),
            where("teamLeadId", "==", userProfile.uid)
        );

        const unsubscribe = onSnapshot(q, 
            (snapshot) => {
                const queriesData = snapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
                // In-memory sort by createdAt descending
                queriesData.sort((a, b) => {
                    const aTime = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : Date.now());
                    const bTime = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : Date.now());
                    return bTime - aTime;
                });
                setTeamQueries(queriesData);
                setLoading(false);
                setError(null);
            },
            (err) => {
                console.error("Error in team queries listener:", err);
                setError(err.message);
                setLoading(false);
            }
        );

        return unsubscribe;
    }, [userProfile?.uid, isLeadUser]);

    // ── Post a query ────────────────────────────────────────────────
    const postQuery = async (title, question, category = "general") => {
        try {
            const leadId = userProfile.teamId || userProfile.managerId || "";
            let leadName = userProfile.teamLeadName || "";
            if (leadId && !leadName) {
                try {
                    const leadDoc = await getDoc(doc(db, "users", leadId));
                    if (leadDoc.exists()) {
                        leadName = leadDoc.data().displayName || "";
                    }
                } catch (fetchLeadErr) {
                    console.error("Failed to fetch lead profile:", fetchLeadErr);
                }
            }

            const newQuery = {
                title,
                question,
                category, // "technical", "process", "general", "career", "other"
                askedBy: userProfile.uid,
                askedByName: userProfile.displayName,
                teamLeadId: leadId, // Lead's ID
                teamLeadName: leadName, // Fetched/set lead name
                status: "Pending", // "Pending", "In Progress", "Resolved"
                replies: [], // Array of replies
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                isNotified: false, // Whether lead has been notified
            };

            const docRef = await addDoc(collection(db, "queries"), newQuery);

            // Notify Team Lead (non-blocking: query must still be saved if notification fails)
            if (newQuery.teamLeadId) {
                try {
                    await addNotification({
    type: "query",
    title: "New Query Submitted",
    message: `${userProfile.displayName} has submitted a new query: "${title}"`,
    recipientId: newQuery.teamLeadId,
    actionUrl: "/lead/queries"
});
                } catch (notifyErr) {
                    // Do not fail the query submission if notification fails
                    console.error("Notification failed for new query:", notifyErr);
                }
            }

            return docRef.id;
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // ── Reply to a query (by team lead) ──────────────────────────
    const replyToQuery = async (queryId, reply) => {
        try {
            const queryRef = doc(db, "queries", queryId);
            const replyData = {
                id: Date.now().toString(),
                repliedBy: userProfile.uid,
                repliedByName: userProfile.displayName,
                repliedByRole: userProfile.role,
                reply,
                createdAt: new Date(),
            };

            const queryDoc = await getDoc(queryRef);
            if (!queryDoc.exists()) {
                throw new Error("Query not found");
            }
            const currentReplies = queryDoc.data()?.replies || [];
            const queryOwnerId = queryDoc.data()?.askedBy;

            await updateDoc(queryRef, {
                replies: [...currentReplies, replyData],
                status: "In Progress",
                updatedAt: serverTimestamp(),
            });

            // Notify Employee
            if (queryOwnerId) {
                await addNotification({
                    type: "success",
                    title: "Query Reply",
                    message: `${userProfile.displayName} replied to your query`,
                    recipientId: query.OwnerId,
                    link: "/employee/queries"
                });
            }
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // ── Update query status ─────────────────────────────────────
    const updateQueryStatus = async (queryId, status) => {
        try {
            const queryRef = doc(db, "queries", queryId);
            await updateDoc(queryRef, {
                status, // "Pending", "In Progress", "Resolved"
                updatedAt: serverTimestamp(),
            });

            // Optionally, we could notify here too if needed, but keeping it simple for now.
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    // ── Delete query ────────────────────────────────────────────
    const deleteQuery = async (queryId) => {
        try {
            await deleteDoc(doc(db, "queries", queryId));
        } catch (err) {
            setError(err.message);
            throw err;
        }
    };

    return (
        <QueryContext.Provider
            value={{
                myQueries,
                teamQueries,
                loading,
                error,
                postQuery,
                replyToQuery,
                updateQueryStatus,
                deleteQuery,
            }}
        >
            {children}
        </QueryContext.Provider>
    );
}
